import React, { useEffect, useState, useRef } from 'react';
import { MapPin, Calendar, CheckCircle, Eye, ExternalLink, FileText, ChevronLeft, ChevronRight, X, Camera } from 'lucide-react';
import { portfolioProjects } from '../mock';

// Small optimized preview tiles (real photos of each project's works) for the card mosaic
const previewThumbs = {
  1: ['/portfolio/thumbs/p1-1.webp', '/portfolio/thumbs/p1-2.webp'],
  2: ['/portfolio/thumbs/p2-1.webp', '/portfolio/thumbs/p2-2.webp'],
  3: ['/portfolio/thumbs/p3-1.webp', '/portfolio/thumbs/p3-2.webp'],
  5: ['/portfolio/thumbs/p5-1.webp', '/portfolio/thumbs/p5-2.webp'],
  6: ['/portfolio/thumbs/p6-1.webp', '/portfolio/thumbs/p6-2.webp'],
  7: ['/portfolio/thumbs/p7-1.webp', '/portfolio/thumbs/p7-2.webp'],
  8: ['/portfolio/thumbs/p8-1.webp', '/portfolio/thumbs/p8-2.webp'],
  9: ['/portfolio/thumbs/p9-1.webp'],
  10: ['/portfolio/thumbs/p10-1.webp', '/portfolio/thumbs/p10-2.webp'],
  11: ['/portfolio/thumbs/p11-1.webp', '/portfolio/thumbs/p11-2.webp'],
};

const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const gridRef = useRef(null);

  // Genuine scroll-reveal for the project grid (with a safety fallback so cards never stay hidden)
  useEffect(() => {
    const el = gridRef.current;
    if (!el || !('IntersectionObserver' in window)) { setRevealed(true); return; }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setRevealed(true); obs.disconnect(); }
    }, { threshold: 0.12 });
    obs.observe(el);
    const fallback = setTimeout(() => setRevealed(true), 1200);
    return () => { obs.disconnect(); clearTimeout(fallback); };
  }, []);

  const ProjectModal = ({ project, onClose }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    
    if (!project) return null;
    
    // Create gallery array with main image + additional gallery images
    const allImages = [
      {
        url: project.image,
        caption: `${project.title} - Main View`,
        description: project.description
      },
      ...(project.galleryImages || []).map((img, index) => ({
        url: img,
        caption: `${project.title} - View ${index + 2}`,
        description: `Additional view of ${project.title} project`
      }))
    ];

    const nextImage = () => {
      console.log('Next image clicked, current:', currentImageIndex); // Debug log
      setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
    };

    const prevImage = () => {
      console.log('Previous image clicked, current:', currentImageIndex); // Debug log
      setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
    };

    const goToImage = (index) => {
      console.log('Go to image:', index); // Debug log
      setCurrentImageIndex(index);
    };

    useEffect(() => {
      setCurrentImageIndex(0); // Reset to first image when modal opens
    }, [project]);

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="heading-2">{project.title}</h3>
            <button className="close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
          
          {/* Enhanced Image Gallery */}
          <div className="modal-gallery">
            <div className="gallery-main">
              <div className="gallery-image-container">
                <img 
                  key={currentImageIndex}
                  src={allImages[currentImageIndex]?.url} 
                  alt={allImages[currentImageIndex]?.caption}
                  className="gallery-main-image"
                  width="800"
                  height="600"
                  onError={() => {
                    console.warn('Gallery image failed to load:', allImages[currentImageIndex]?.url);
                  }}
                  style={{ 
                    width: '100%',
                    height: 'auto',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    animation: 'imageSlideIn 0.4s ease-out',
                    filter: 'brightness(1.02) contrast(1.05)',
                    backgroundColor: '#f8f9fa'
                  }}
                />
                
                {/* Navigation Arrows */}
                {allImages.length > 1 && (
                  <>
                    <button 
                      className="gallery-nav gallery-nav-prev" 
                      onClick={(e) => {
                        e.stopPropagation();
                        prevImage();
                      }}
                      type="button"
                    >
                      <ChevronLeft size={28} />
                    </button>
                    <button 
                      className="gallery-nav gallery-nav-next" 
                      onClick={(e) => {
                        e.stopPropagation();
                        nextImage();
                      }}
                      type="button"
                    >
                      <ChevronRight size={28} />
                    </button>
                  </>
                )}
                
                {/* Image Counter */}
                {allImages.length > 1 && (
                  <div className="image-counter">
                    {currentImageIndex + 1} / {allImages.length}
                  </div>
                )}
              </div>
              
              {/* Thumbnail Navigation */}
              {allImages.length > 1 && (
                <div className="gallery-thumbnails">
                  {allImages.map((img, index) => (
                    <button
                      key={index}
                      className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        goToImage(index);
                      }}
                      type="button"
                    >
                      <img 
                        src={img.url} 
                        alt={img.caption} 
                        width="80"
                        height="60"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Image Caption */}
            <div className="image-caption">
              <h4 className="body-medium" style={{ fontWeight: '600', marginBottom: '4px' }}>
                {allImages[currentImageIndex]?.caption}
              </h4>
              <p className="body-small" style={{ color: 'var(--text-light)' }}>
                {allImages[currentImageIndex]?.description}
              </p>
            </div>
          </div>
          
          <div className="modal-body">
            <div className="project-meta">
              <div className="meta-item">
                <MapPin size={16} color="var(--text-light)" />
                <span className="body-small">{project.location}</span>
              </div>
              <div className="meta-item">
                <Calendar size={16} color="var(--text-light)" />
                <span className="body-small">{project.year}</span>
              </div>
            </div>
            
            <p className="body-medium" style={{ marginBottom: 'var(--spacing-medium)' }}>
              {project.detailedDescription || project.description}
            </p>
            
            {project.actualImages && (
              <div className="actual-images-section">
                <h4 className="heading-3" style={{ marginBottom: 'var(--spacing-small)' }}>
                  <FileText size={20} style={{ marginRight: '8px', display: 'inline' }} />
                  Authentic Project Features from PDF
                </h4>
                <div className="actual-images-list">
                  {project.actualImages.map((imageDesc, idx) => (
                    <div key={idx} className="image-description">
                      <Camera size={14} color="var(--brand-primary)" />
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {imageDesc}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="highlights-section">
              <h4 className="heading-3" style={{ marginBottom: 'var(--spacing-small)' }}>
                Project Highlights
              </h4>
              <ul className="highlights-list">
                {project.highlights.map((highlight, idx) => (
                  <li key={idx} className="body-medium">
                    {highlight}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="client-info">
              <h4 className="body-medium" style={{ fontWeight: '600', marginBottom: 'var(--spacing-xs)' }}>
                Client: {project.client}
              </h4>
              <span className="status-badge">
                <CheckCircle size={14} />
                {project.status}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section id="portfolio" className="section-padding" style={{ background: 'var(--bg-subtle)' }}>
      <div className="container">
        <div className="portfolio-header fade-in visible">
          <h2 className="heading-1" style={{ marginBottom: 'var(--spacing-medium)', textAlign: 'center' }}>
            Our <span style={{ color: 'var(--brand-accent)' }}>Portfolio</span>
          </h2>
          <p className="body-large" style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto var(--spacing-medium)' }}>
            Discover our flagship projects across India, where we've transformed natural spaces into inspiring 
            destinations that educate, engage, and promote conservation.
          </p>
          
          {/* Portfolio Hero Image */}
          <div className="portfolio-hero-image" style={{ 
            textAlign: 'center', 
            marginBottom: 'var(--spacing-giant)',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(27, 67, 50, 0.2)'
          }}>
            <img 
              src="/portfolio/showcase.webp"
              alt="Forest Vision Alliance Portfolio Showcase - Butterfly sculptures, eco-tourism huts, interpretation centers, and wildlife installations"
              width="1200"
              height="500"
              loading="lazy"
              decoding="async"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '500px',
                objectFit: 'cover',
                borderRadius: '24px'
              }}
            />
          </div>
          
        </div>
        
        <div className="portfolio-grid" ref={gridRef}>
          {portfolioProjects.map((project, index) => (
            <div 
              key={project.id} 
              className={`portfolio-card fade-in ${revealed ? 'visible' : ''}`}
              style={{ transitionDelay: `${index * 90}ms` }}
            >
              <div 
                className="project-image"
                onClick={() => setSelectedProject(project)}
                data-testid={`portfolio-card-image-${project.id}`}
              >
                {(() => {
                  const tiles = previewThumbs[project.id] || [];
                  const count = tiles.length + 1;
                  return (
                    <div className="project-mosaic" data-count={count}>
                      <img
                        className="mosaic-main"
                        src={`/portfolio/p${project.id}.webp`}
                        alt={`${project.title} - Forest Vision Alliance Project`}
                        loading="lazy"
                        decoding="async"
                      />
                      {tiles.map((t, i) => (
                        <img
                          key={i}
                          className="mosaic-side"
                          src={t}
                          alt={`${project.title} work ${i + 2}`}
                          loading="lazy"
                          decoding="async"
                        />
                      ))}
                    </div>
                  );
                })()}
                <div className="photo-count-badge" data-testid={`portfolio-photo-count-${project.id}`}>
                  <Camera size={12} />
                  <span>{(project.galleryImages?.length || 0) + 1}</span>
                </div>
                <div className="gallery-pill" data-testid={`portfolio-view-gallery-${project.id}`}>
                  <Eye size={14} />
                  <span>View Gallery</span>
                </div>
              </div>
              
              <div className="project-content">
                <div className="project-header">
                  <div className="project-status">
                    <CheckCircle size={16} color="var(--brand-primary)" />
                    <span className="body-small" style={{ color: 'var(--brand-primary)', fontWeight: '600' }}>
                      {project.status}
                    </span>
                  </div>
                  <div className="project-category">
                    <span className="body-small" style={{ 
                      background: 'var(--brand-accent)', 
                      color: 'white', 
                      padding: '4px 12px', 
                      borderRadius: '12px',
                      fontWeight: '600'
                    }}>
                      {project.category}
                    </span>
                  </div>
                </div>
                
                <h3 className="heading-3" style={{ marginBottom: 'var(--spacing-small)' }}>
                  {project.title}
                </h3>
                
                <div className="project-meta">
                  <div className="meta-item">
                    <MapPin size={16} color="var(--text-light)" />
                    <span className="body-small" style={{ color: 'var(--text-light)' }}>
                      {project.location}
                    </span>
                  </div>
                  <div className="meta-item">
                    <Calendar size={16} color="var(--text-light)" />
                    <span className="body-small" style={{ color: 'var(--text-light)' }}>
                      {project.year}
                    </span>
                  </div>
                </div>
                
                <p className="body-medium" style={{ marginBottom: 'var(--spacing-medium)', color: 'var(--text-light)' }}>
                  {project.description}
                </p>
                
                <div className="feature-chips" data-testid={`project-feature-chips-${project.id}`}>
                  {project.highlights.slice(0, 3).map((highlight, idx) => (
                    <span key={idx} className="feature-chip">{highlight}</span>
                  ))}
                </div>
                
                <button 
                  className="view-more-btn"
                  onClick={() => setSelectedProject(project)}
                >
                  View Photo Gallery
                  <ExternalLink size={16} style={{ marginLeft: '8px' }} />
                </button>
              </div>
            </div>
          ))}
        </div>
        
        <div className="portfolio-cta fade-in visible" style={{ transitionDelay: '800ms' }}>
          <h3 className="heading-2" style={{ marginBottom: 'var(--spacing-medium)', textAlign: 'center' }}>
            Ready to Create Your Legacy Project?
          </h3>
          <p className="body-large" style={{ textAlign: 'center', marginBottom: 'var(--spacing-large)' }}>
            Join us in building something extraordinary that will inspire generations to come.
          </p>
          <div className="cta-buttons">
            <a href="#contact" className="btn-primary">
              Start Your Project
            </a>
          </div>
        </div>
      </div>
      
      {selectedProject && <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />}
      
      <style jsx>{`
        .portfolio-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: var(--spacing-large);
          margin-bottom: var(--spacing-giant);
        }
        
        .portfolio-card {
          background: var(--bg-card);
          border-radius: 4px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(27, 67, 50, 0.1);
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          position: relative;
        }
        
        .portfolio-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 28px 60px rgba(26, 33, 24, 0.18);
          background: var(--bg-card);
        }
        
        .project-image {
          position: relative;
          height: 250px;
          overflow: hidden;
          cursor: pointer;
        }
        
        .project-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.6s cubic-bezier(0.25, 1, 0.5, 1);
          background-color: #eceae3;
          display: block;
        }

        /* Mosaic preview of project works */
        .project-mosaic {
          display: grid;
          gap: 4px;
          width: 100%;
          height: 100%;
        }
        .project-mosaic[data-count="1"] {
          grid-template-columns: 1fr;
        }
        .project-mosaic[data-count="2"] {
          grid-template-columns: 1.7fr 1fr;
        }
        .project-mosaic[data-count="3"] {
          grid-template-columns: 1.7fr 1fr;
          grid-template-rows: 1fr 1fr;
        }
        .project-mosaic[data-count="3"] .mosaic-main {
          grid-row: 1 / span 2;
        }
        .portfolio-card:hover .mosaic-main {
          transform: scale(1.06);
        }
        .portfolio-card:hover .mosaic-side {
          transform: scale(1.08);
        }

        .image-loading-placeholder {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          color: var(--text-light);
          font-size: 14px;
          z-index: 1;
        }

        .loading-spinner {
          width: 24px;
          height: 24px;
          border: 2px solid #e9ecef;
          border-top: 2px solid var(--brand-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: var(--spacing-small);
        }

        .image-error-placeholder {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          color: var(--text-secondary);
          text-align: center;
          padding: var(--spacing-medium);
        }

        .error-icon {
          font-size: 2rem;
          margin-bottom: var(--spacing-small);
          opacity: 0.5;
        }

        .image-error-placeholder span {
          font-weight: 600;
          margin-bottom: var(--spacing-xs);
        }

        .image-error-placeholder small {
          color: var(--text-light);
          font-size: 12px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .portfolio-card:hover .project-image img {
          filter: brightness(1.06) saturate(1.05);
        }
        
        .photo-count-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(26, 33, 24, 0.6);
          color: #fff;
          padding: 5px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 5px;
          backdrop-filter: blur(6px);
          pointer-events: none;
        }

        .gallery-pill {
          position: absolute;
          left: 12px;
          bottom: 12px;
          background: rgba(255, 255, 255, 0.92);
          color: var(--brand-primary);
          padding: 7px 14px;
          border-radius: 999px;
          font-size: 0.8rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 7px;
          backdrop-filter: blur(8px);
          box-shadow: 0 4px 14px rgba(26, 33, 24, 0.18);
          transition: background-color 0.3s ease, color 0.3s ease, transform 0.4s cubic-bezier(0.25, 1, 0.5, 1);
          pointer-events: none;
        }

        .portfolio-card:hover .gallery-pill {
          background: var(--brand-primary);
          color: #fff;
          transform: translateY(-2px);
        }
        
        .project-content {
          padding: var(--spacing-large);
          position: relative;
        }
        
        .project-content:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--brand-primary), var(--brand-accent));
          transition: width 0.5s ease;
        }
        
        .portfolio-card:hover .project-content:before {
          width: 100%;
        }
        
        .project-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-medium);
        }
        
        .project-status {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          animation: fadeInUp 0.6s ease-out;
        }
        
        .project-meta {
          display: flex;
          gap: var(--spacing-medium);
          margin-bottom: var(--spacing-medium);
        }
        
        .meta-item {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          transition: transform 0.3s ease;
        }
        
        .meta-item:hover {
          transform: scale(1.1);
        }
        
        .feature-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: var(--spacing-medium);
        }

        .feature-chip {
          font-size: 0.78rem;
          font-weight: 500;
          color: var(--text-secondary);
          background: var(--bg-subtle);
          border: 1px solid var(--border-light);
          padding: 6px 12px;
          border-radius: 999px;
          line-height: 1.2;
          cursor: default;
          transition: color 0.3s ease, background-color 0.3s ease, border-color 0.3s ease, transform 0.3s cubic-bezier(0.25, 1, 0.5, 1);
        }

        .feature-chip:hover {
          color: #fff;
          background: var(--brand-primary);
          border-color: var(--brand-primary);
          transform: translateY(-2px);
        }
        
        @keyframes sparkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          25% { opacity: 0.8; transform: scale(1.1); }
          50% { opacity: 0.5; transform: scale(0.9); }
          75% { opacity: 0.8; transform: scale(1.05); }
        }
        
        @keyframes floatIn {
          0% { 
            opacity: 0; 
            transform: translateY(30px) scale(0.9); 
          }
          100% { 
            opacity: 1; 
            transform: translateY(0) scale(1); 
          }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .portfolio-card {
          position: relative;
          overflow: hidden;
        }
        
        .portfolio-card::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(45deg, 
            transparent 30%, 
            rgba(255, 255, 255, 0.1) 50%, 
            transparent 70%);
          transform: translateX(-100%) translateY(-100%);
          transition: transform 0.8s ease;
          z-index: 1;
          pointer-events: none;
        }
        
        .portfolio-card:hover::before {
          transform: translateX(0) translateY(0);
        }
        
        .portfolio-card::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, 
            rgba(45, 90, 45, 0.02) 0%, 
            transparent 50%, 
            rgba(70, 130, 60, 0.02) 100%);
          opacity: 0;
          transition: opacity 0.5s ease;
          z-index: 0;
          pointer-events: none;
        }
        
        .portfolio-card:hover::after {
          opacity: 1;
        }
        
        .view-more-btn {
          background: linear-gradient(135deg, transparent, var(--brand-primary));
          border: 2px solid var(--brand-primary);
          border-radius: 25px;
          padding: 12px 24px;
          color: var(--brand-primary);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          margin-top: var(--spacing-medium);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          position: relative;
          overflow: hidden;
        }
        
        .view-more-btn:before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          transition: left 0.5s;
        }
        
        .view-more-btn:hover {
          background: var(--brand-primary);
          color: white;
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 8px 25px rgba(45, 90, 45, 0.3);
        }
        
        .view-more-btn:hover:before {
          left: 100%;
        }
        
        .portfolio-cta {
          background: var(--bg-card);
          padding: var(--spacing-giant);
          border-radius: 32px;
          text-align: center;
        }
        
        .cta-buttons {
          display: flex;
          justify-content: center;
        }
        
        /* Enhanced Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100000;
          padding: var(--spacing-medium);
          backdrop-filter: blur(8px);
          animation: modalFadeIn 0.3s ease-out;
        }
        
        @keyframes modalFadeIn {
          0% { 
            opacity: 0; 
            backdrop-filter: blur(0px);
          }
          100% { 
            opacity: 1; 
            backdrop-filter: blur(8px);
          }
        }
        
        .modal-content {
          background: white;
          border-radius: 24px;
          max-width: 1120px;
          width: 100%;
          max-height: 95vh;
          overflow-y: auto;
          position: relative;
          box-shadow: 0 30px 60px rgba(0, 0, 0, 0.6);
          animation: modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        @keyframes modalSlideIn {
          0% { 
            transform: scale(0.8) translateY(50px);
            opacity: 0;
          }
          100% { 
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes imageSlideIn {
          0% { 
            opacity: 0;
            transform: translateX(30px) scale(0.95);
          }
          100% { 
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--spacing-large);
          border-bottom: 1px solid var(--border-light);
          background: var(--bg-subtle);
          border-radius: 24px 24px 0 0;
        }
        
        .close-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 50%;
          transition: background 0.2s ease;
          color: var(--text-primary);
        }
        
        .close-btn:hover {
          background: var(--bg-card);
        }
        
        /* Gallery Styles */
        .modal-gallery {
          padding: var(--spacing-large);
        }
        
        .gallery-main {
          margin-bottom: var(--spacing-medium);
        }
        
        .gallery-image-container {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: var(--spacing-medium);
          background: #14160f;
        }
        
        .gallery-main-image {
          width: 100%;
          height: 68vh;
          max-height: 760px;
          object-fit: contain;
          transition: transform 0.5s ease, filter 0.3s ease;
          border-radius: 12px;
        }
        
        .gallery-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.95);
          border: none;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--brand-primary);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(10px);
          z-index: 10;
          pointer-events: auto;
        }
        
        .gallery-nav:hover {
          background: var(--brand-primary);
          color: white;
          transform: translateY(-50%) scale(1.15) rotate(5deg);
          box-shadow: 0 12px 30px rgba(45, 90, 45, 0.4);
        }
        
        .gallery-nav:active {
          transform: translateY(-50%) scale(1.05);
        }
        
        .gallery-nav-prev {
          left: 16px;
        }
        
        .gallery-nav-next {
          right: 16px;
        }
        
        .image-counter {
          position: absolute;
          bottom: 16px;
          right: 16px;
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }
        
        .gallery-thumbnails {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
          gap: 8px;
          padding: 8px 2px;
          max-height: 260px;
          overflow-y: auto;
        }
        
        .thumbnail {
          width: 100%;
          aspect-ratio: 4 / 3;
          border-radius: 8px;
          overflow: hidden;
          border: 3px solid transparent;
          cursor: pointer;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s ease, box-shadow 0.3s ease;
          background: none;
          padding: 0;
          position: relative;
        }
        
        .thumbnail::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(45, 90, 45, 0.2) 0%, transparent 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .thumbnail.active {
          border-color: var(--brand-primary);
          transform: scale(1.04);
          box-shadow: 0 4px 16px rgba(45, 90, 45, 0.3);
        }
        
        .thumbnail.active::before {
          opacity: 1;
        }
        
        .thumbnail:hover {
          border-color: var(--brand-accent);
          box-shadow: 0 6px 20px rgba(70, 130, 60, 0.2);
        }
        
        .thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .image-caption {
          background: var(--bg-subtle);
          padding: var(--spacing-medium);
          border-radius: 12px;
          margin-bottom: var(--spacing-medium);
        }
        
        .modal-body {
          padding: 0 var(--spacing-large) var(--spacing-large);
        }
        
        .actual-images-section {
          background: var(--bg-section);
          padding: var(--spacing-medium);
          border-radius: 16px;
          margin-bottom: var(--spacing-large);
        }
        
        .actual-images-list {
          display: grid;
          gap: var(--spacing-small);
        }
        
        .image-description {
          display: flex;
          align-items: flex-start;
          gap: var(--spacing-small);
          padding: var(--spacing-small);
          background: white;
          border-radius: 8px;
        }
        
        .highlights-section {
          margin-bottom: var(--spacing-large);
        }
        
        .highlights-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .highlights-list li {
          padding: var(--spacing-xs) 0;
          position: relative;
          padding-left: var(--spacing-medium);
          border-bottom: 1px solid var(--bg-subtle);
        }
        
        .highlights-list li:before {
          content: '✓';
          color: var(--brand-primary);
          font-weight: bold;
          position: absolute;
          left: 0;
        }
        
        .client-info {
          background: var(--bg-section);
          padding: var(--spacing-medium);
          border-radius: 16px;
        }
        
        .status-badge {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          color: var(--brand-primary);
          font-weight: 600;
        }
        
        /* Mobile-specific optimizations */
        @media (max-width: 768px) {
          .portfolio-grid {
            grid-template-columns: 1fr;
            gap: var(--spacing-medium);
          }

          .project-image {
            height: 220px; /* Reduced height for mobile */
          }

          .project-image img {
            object-fit: cover;
            /* Reduce image quality slightly on mobile for faster loading */
            image-rendering: optimizeSpeed;
          }

          .image-loading-placeholder {
            font-size: 12px;
          }

          .loading-spinner {
            width: 20px;
            height: 20px;
          }

          .portfolio-card {
            margin-bottom: var(--spacing-medium);
          }
          
          .project-header {
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-xs);
          }
          
          .project-meta {
            flex-direction: column;
            gap: var(--spacing-xs);
          }
          
          .portfolio-cta {
            padding: var(--spacing-large);
          }
          
          .modal-content {
            margin: var(--spacing-small);
            max-height: 95vh;
          }
          
          .gallery-main-image {
            height: 56vh;
            max-height: 420px;
          }
          
          .gallery-nav {
            width: 40px;
            height: 40px;
          }
          
          .gallery-thumbnails {
            grid-template-columns: repeat(auto-fill, minmax(68px, 1fr));
            max-height: 200px;
          }
        }

        @media (max-width: 480px) {
          .project-image {
            height: 200px; /* Further reduced for small screens */
          }

          .portfolio-card {
            border-radius: 16px;
          }

          .image-loading-placeholder {
            font-size: 11px;
            padding: var(--spacing-small);
          }

          .loading-spinner {
            width: 18px;
            height: 18px;
          }
        }

        /* Performance optimizations */
        .portfolio-card {
          will-change: transform;
          backface-visibility: hidden;
        }

        .project-image img {
          will-change: opacity;
        }

        /* Reduce animations on mobile for better performance */
        @media (max-width: 768px) {
          .portfolio-card:hover {
            transform: translateY(-4px);
          }
          
          .portfolio-card:hover .project-image img {
            transform: scale(1.02);
          }
        }
      `}</style>
    </section>
  );
};

export default Portfolio;